import React, { ReactNode, useEffect, useRef, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { Card } from '@/components/yoda/components/Card/Card';
import { Typography } from '@/components/yoda/components/Typography';
import { Button } from '@/components/yoda/components/Button/Button';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/GCPAuthService';
import { hasAnyTenantRole } from '../utils/authUtils';
import { authConfig } from '@/shared/config/auth.config';

// Lazy load AuthCallback to handle OAuth callback processing
const AuthCallback = lazy(() => import('../routes/auth-callback'));

// Helper to check if we're on the OAuth callback route with params
const isOAuthCallbackWithParams = (pathname: string): boolean => {
  if (pathname !== '/auth/callback') return false;
  if (typeof window === 'undefined') return false;
  const search = window.location.search;
  return (
    search.includes('auth_token') ||
    search.includes('user_id') ||
    search.includes('error')
  );
};

interface AuthGuardProps {
  children: ReactNode;
  fallbackUrl?: string;
  requiredPermission?: string;
  requiredRoles?: string[];
  unauthorizedRedirect?: string;
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  fallbackUrl = '/login',
  requiredPermission = 'assistant-hub:access',
  requiredRoles,
  unauthorizedRedirect = '/',
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    user,
    checkAuthExpiry,
    refreshAuthToken,
    initializeFromStoredToken,
    isLoggingOut,
  } = useAuthStore();
  const { t } = useTranslation();
  const [isChecking, setIsChecking] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const assistantToastShownRef = useRef(false);
  const requiresRole = Boolean(requiredRoles && requiredRoles.length > 0);
  const hasRequiredRole =
    !requiresRole || hasAnyTenantRole(user, requiredRoles as string[]);

  // Check if this is the OAuth callback route with params
  const shouldRenderCallback = isOAuthCallbackWithParams(location.pathname);

  useEffect(() => {
    // Early return if logging out - don't interfere with logout process
    if (isLoggingOut) {
      setIsChecking(false);
      return;
    }

    const verifyAuth = async () => {
      try {
        setIsChecking(true);
        setError(null);

        // First, try to initialize from stored token if not authenticated
        if (!isAuthenticated) {
          await initializeFromStoredToken();
        }

        // Check if auth is still valid
        const isValid = checkAuthExpiry();

        // If token is expired but we have a stored token, try to refresh
        if (!isValid && authService.getStoredToken()) {
          await refreshAuthToken();
        }
      } catch (err) {
        setError(t('auth.error.verificationFailed'));
        console.error('Auth verification failed:', err);
      } finally {
        setIsChecking(false);
      }
    };

    verifyAuth();
  }, [
    location.pathname,
    location.search,
    checkAuthExpiry,
    isAuthenticated,
    refreshAuthToken,
    initializeFromStoredToken,
    isLoggingOut,
    t,
  ]);

  // Handle unauthenticated redirect
  useEffect(() => {
    // Don't redirect if logging out
    if (isLoggingOut) {
      return;
    }

    if (!isChecking && !isAuthenticated) {
      const isAuthRoute =
        location.pathname === fallbackUrl ||
        location.pathname.startsWith('/auth');

      if (!isAuthRoute && typeof window !== 'undefined') {
        const searchParams = new URLSearchParams(window.location.search);
        const hasAssistantParam =
          searchParams.has('assistant') || searchParams.has('edit');
        if (hasAssistantParam && !assistantToastShownRef.current) {
          sessionStorage.setItem(
            'assistantAuthMessage',
            'Please login to access the assistant.'
          );
          assistantToastShownRef.current = true;
        }
        try {
          const { pathname, search, hash } = window.location;
          const redirectTarget = `${pathname}${search || ''}${hash || ''}`;
          sessionStorage.setItem(
            authConfig.storage.postLoginRedirectKey,
            redirectTarget
          );
        } catch {
          // Ignore storage errors - redirect will work without saving state
        }
      }
      // Add a small delay to ensure router is ready
      const redirectTimer = setTimeout(() => {
        // Use TanStack Router navigation
        try {
          navigate({ to: fallbackUrl, replace: true });
        } catch (error) {
          console.error(
            '[AuthGuard] TanStack Router navigation failed:',
            error
          );
          // Don't fall back to window.location - let the router handle it
          // The navigation service will be used by other components if needed
        }
      }, 100); // 100ms delay to ensure router initialization

      return () => clearTimeout(redirectTimer);
    }
  }, [isChecking, isAuthenticated, isLoggingOut, navigate, fallbackUrl]);

  const handleRetry = () => {
    // Force a re-render by resetting state and re-running auth check
    setIsChecking(true);
    setError(null);
    // Navigate to the same location to trigger re-render
    navigate({ to: location.pathname, replace: true });
  };

  // Handle missing tenant-role redirect
  useEffect(() => {
    if (
      requiresRole &&
      !isChecking &&
      isAuthenticated &&
      !isLoggingOut &&
      !hasRequiredRole
    ) {
      const timer = setTimeout(() => {
        navigate({ to: unauthorizedRedirect, replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }

    return;
  }, [
    requiresRole,
    hasRequiredRole,
    isChecking,
    isAuthenticated,
    isLoggingOut,
    navigate,
    unauthorizedRedirect,
    requiredRoles,
  ]);

  // If on OAuth callback route with params, render AuthCallback to process the OAuth response
  if (shouldRenderCallback) {
    return (
      <Suspense fallback={null}>
        <AuthCallback />
      </Suspense>
    );
  }

  // Show loading state
  if (isChecking) {
    return (
      // <LoadingContainer $minHeight='50vh' $gap='24px' $flexDirection='column'>
      //   <Progress variant='circular' />
      //   <Typography variant='large' color='secondary'>
      //     {t('auth.verifying')}
      //   </Typography>
      // </LoadingContainer>
      <></>
    );
  }

  // Show error state
  if (error) {
    return (
      <div
        className='flex min-h-[50vh] items-center justify-center p-6'
        role='alert'
        aria-live='assertive'
        data-testid='auth-guard-error'
      >
        <Card className='max-w-[500px] p-8 text-center'>
          <div className='flex flex-col items-center gap-4'>
            <Typography
              variant='h3'
              color='danger'
              data-testid='auth-error-title'
            >
              {t('auth.error.title')}
            </Typography>
            <Typography
              variant='p'
              color='secondary'
              data-testid='auth-error-message'
            >
              {error}
            </Typography>
            <Button
              variant='default'
              onClick={handleRetry}
              data-testid='auth-retry-button'
            >
              {t('auth.retry')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Return empty while navigation happens via useEffect
    // This prevents rendering protected content before redirect
    return null;
  }

  // Check if user has required permission
  const hasPermission =
    user &&
    (!requiredPermission ||
      user.permissions?.includes(requiredPermission) ||
      // Default: all authenticated users have access to assistant-hub
      (requiredPermission === 'assistant-hub:access' && !user.permissions));

  if (requiredPermission && !hasPermission) {
    return (
      <div
        className='flex min-h-[50vh] items-center justify-center p-6'
        role='alert'
        aria-live='assertive'
        data-testid='auth-guard-permission-denied'
      >
        <Card className='max-w-[500px] p-8 text-center'>
          <div className='flex flex-col items-center gap-4'>
            <Typography
              variant='h3'
              color='danger'
              data-testid='auth-denied-title'
            >
              {t('auth.accessDenied.title')}
            </Typography>
            <Typography
              variant='p'
              color='secondary'
              data-testid='auth-denied-message'
            >
              {t('auth.accessDenied.noPermission')}
            </Typography>
            <Typography
              variant='small'
              color='secondary'
              data-testid='auth-denied-permission'
            >
              {t('auth.accessDenied.requiredPermission', {
                permission: requiredPermission,
              })}
            </Typography>
            <Button
              variant='default'
              onClick={() =>
                navigate({
                  to: '/',
                  search: {
                    chat: undefined,
                    assistant: undefined,
                    assistantError: undefined,
                  },
                })
              }
              data-testid='auth-go-home-button'
            >
              {t('auth.goHome')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (requiresRole && !hasRequiredRole) {
    return null;
  }

  // User is authenticated and has permission and role
  return <>{children}</>;
};

export default AuthGuard;
